import type { CharacterLocalSettingInterface, WorldInterface } from "@/types/worlds";

// A character-local setting is carried on the character, but every rules consumer in the app
// reads a world off the app store. This module is the only place that converts between the two,
// and it deliberately imports no store so it can be unit-tested without mounting anything.

// The blob uuid is namespaced because it keys additionalData.choices alongside real world uuids
// (see resolveWorldChoiceSets). Since the prefix has to exist anyway, it doubles as the marker
// that tells newCharacter() and ItemFinder they are holding a carrier rather than a real world.
export const SYNTHETIC_WORLD_PREFIX = 'synthetic-';

// Exactly today's worldless behaviour: ancestries available, frameworks available, strain off.
// A character whose blob still matches these is indistinguishable from one with no blob at all.
export const LOCAL_SETTING_DEFAULT_FLAGS = {
    ancestries: true,
    frameworks: true,
    strain: false,
} as const;

// The build options a free user may author in their own setting. Paid tiers get the full list.
// Lives here, not in the panel, so the component and its tests share one source of truth — tuning
// the list updates both at once instead of leaving a test asserting a stale array.
export const FREE_USER_BUILD_OPTION_PATHS = [
    'buildPoints',
    'superPowerPoints',
    'languageSelections',
    'startingFunds',
];

// The allow-list BuildOptionConfigurator's `restrictedPaths` should receive for one row.
//
// `paid` is a parameter rather than an auth-store read so this module stays store-free and
// unit-testable; the caller supplies the tier.
//
// Returns undefined (not []) for paid tiers: restrictedPaths is an ALLOW-list and the configurator
// shows everything when it is absent, so an empty array would hide every option.
//
// `currentPath` is always included. Seeding from a template can leave a free user holding a build
// option on a path they cannot author — without this its <select> has no matching <option> and
// renders blank, which reads as data loss. Including it keeps the row honest about what it is, and
// lets the user switch back after looking at the alternatives.
export function buildOptionPathsFor(
    currentPath: string | null | undefined,
    paid: boolean,
): string[] | undefined {
    if (paid) return undefined;

    // Copied, never the shared constant itself — a consumer mutating the returned array would
    // otherwise edit the allow-list for every other row and every later call.
    if (!currentPath || FREE_USER_BUILD_OPTION_PATHS.includes(currentPath)) {
        return [...FREE_USER_BUILD_OPTION_PATHS];
    }

    return [...FREE_USER_BUILD_OPTION_PATHS, currentPath];
}

export function isSyntheticWorldUuid(uuid: string | null | undefined): boolean {
    return typeof uuid === 'string' && uuid.startsWith(SYNTHETIC_WORLD_PREFIX);
}

export function newCharacterLocalSetting(uuid: string): CharacterLocalSettingInterface {
    return {
        uuid,
        sourceName: null,
        settingRules: [],
        modifications: [],
        flags: { ...LOCAL_SETTING_DEFAULT_FLAGS },
    };
}

// "Empty" means "says nothing a bare system default does not already say". Flags count: toggling
// one with no rules present is still a real setting choice, and must keep store.world alive.
//
// A name counts too. It changes no build maths, but it is something the user chose and expects to
// see: without it, reverting every rule and flag to its default made the setting empty and the
// Toolbar badge dropped the name along with them. The cost is accepted deliberately — a named,
// otherwise-default setting now hydrates a synthetic world carrying zero modifications, purely so
// the name has somewhere to live.
//
// Blank is not named. The panel already normalizes a cleared field to null, but this predicate
// also runs against settings straight off the API, so it answers the question the same way rather
// than trusting that edge.
export function isEmptyLocalSetting(
    setting: CharacterLocalSettingInterface | null | undefined,
): boolean {
    if (!setting) return true;
    if ((setting.sourceName ?? '').trim().length > 0) return false;
    if ((setting.settingRules ?? []).length > 0) return false;
    if ((setting.modifications ?? []).length > 0) return false;

    const flags = { ...LOCAL_SETTING_DEFAULT_FLAGS, ...(setting.flags ?? {}) };

    return flags.ancestries === LOCAL_SETTING_DEFAULT_FLAGS.ancestries
        && flags.frameworks === LOCAL_SETTING_DEFAULT_FLAGS.frameworks
        && flags.strain === LOCAL_SETTING_DEFAULT_FLAGS.strain;
}

// `name` is a parameter rather than a store read so this module stays store-free. Callers pass
// setting.sourceName ?? ux.labels?.swade?.localSetting?.name ?? 'Custom Setting'.
export function localSettingToWorld(
    setting: CharacterLocalSettingInterface | null | undefined,
    name: string,
): WorldInterface | null {
    if (!setting?.uuid) return null;
    if (isEmptyLocalSetting(setting)) return null;

    return {
        uuid: setting.uuid,
        name,
        // Keeps auth.isWorldOwner() false. PlayerAssignment's other guard (store.world?.uuid)
        // stops working the moment a synthetic world is installed, so this is the only thing
        // preventing GET /worlds/synthetic-.../players for a character owner.
        owner: null,
        // Everything is flattened into `modifications` below, so this stays empty — which also
        // means combineSettingRules applies the array in order and rule mods keep losing to
        // authored mods, mirroring how world.modifications beat world.settingRules[].modifications.
        settingRules: [],
        flags: { ...LOCAL_SETTING_DEFAULT_FLAGS, ...(setting.flags ?? {}) },
        modifications: [
            ...(setting.settingRules ?? []).flatMap((rule) => rule.modifications ?? []),
            ...(setting.modifications ?? []),
        ],
        // NO `defaults` key. A synthetic world overrides no defaults, and an empty object would
        // misrepresent one — combineSettingRules now spreads it as a harmless no-op either way.
    };
}
